import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'

import { AreaPoints, WorkItemArea } from '../../data/interfaces/worklog.interface'
import { AREA_META } from '../../data/constants/worklog.constants'
import { splitSum } from '../../data/utils/area-split.util'

let instanceCount = 0

interface SplitRow {
  area: WorkItemArea
  label: string
  icon: string
  color: string
  value: number
  pct: number
}

/**
 * Editor del reparto de puntos entre las áreas que tocó una tarea (F-156): «de estos 10 puntos,
 * ¿cuántos fueron de la API y cuántos del panel?». Así los frentes de las métricas suman el total.
 *
 * Es CONTROLADO: el dueño le pasa el reparto y lo siembra él (partes iguales al cambiar puntos o
 * áreas); el componente solo emite cuando la persona escribe. Emitir desde `ngOnChanges` cambiaría
 * el estado del padre a media detección de cambios.
 */
@Component({
  selector: 'app-worklog-area-split',
  templateUrl: './worklog-area-split.component.html',
  styleUrls: ['./worklog-area-split.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorklogAreaSplitComponent implements OnChanges {
  @Input() points: number | null = null
  @Input() areas: WorkItemArea[] = []
  @Input() split: AreaPoints | null = null
  @Output() splitChange = new EventEmitter<AreaPoints>()

  /** Prefijo único por instancia: el detalle puede pintar dos editores y los `id` no deben chocar. */
  readonly uid = `as-${++instanceCount}`

  rows: SplitRow[] = []
  sum = 0
  diff = 0

  ngOnChanges(): void {
    const total = this.points ?? 0
    this.sum = splitSum(this.split)
    this.diff = total - this.sum
    this.rows = [...new Set(this.areas)].map((area) => {
      const value = this.split?.[area] ?? 0
      const meta = AREA_META[area]
      return {
        area,
        label: meta.label,
        icon: meta.icon,
        color: meta.color,
        value,
        pct: total ? Math.min(100, Math.round((value / total) * 100)) : 0,
      }
    })
  }

  /** Un número escrito a mano: entero, nunca negativo. */
  setValue(area: WorkItemArea, raw: string | number): void {
    const n = Math.max(0, Math.floor(Number(raw) || 0))
    this.splitChange.emit({ ...(this.split ?? {}), [area]: n })
  }

  trackArea(_: number, r: SplitRow): string {
    return r.area
  }
}
